const FIELD_ORDER = {
  tops: ['chest', 'length'],
  bottoms: ['waist', 'hip', 'inseam'],
};
const WIDTH_FIELDS = new Set(['chest', 'waist', 'hip']);
const scrollLocks = new Set();
let previousOverflow = '';
let instanceNumber = 0;

function plain(value) {
  return String(value ?? '').normalize('NFKC').toLowerCase()
    .replace(/ß/g, 'ss').normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/\([^)]*\)/g, '').trim().replace(/\s+/g, ' ');
}

export function cmFromValue(value, unit = 'cm') {
  const text = String(value ?? '').trim();
  if (!/^(?:\d+(?:[.,]\d+)?|[.,]\d+)$/.test(text)) return null;
  const number = Number(text.replace(',', '.'));
  if (!Number.isFinite(number) || number <= 0 || !['cm', 'inch'].includes(unit)) return null;
  const cm = unit === 'inch' ? number * 2.54 : number;
  return Number.isFinite(cm) ? cm : null;
}

export function normalizeSize(value) {
  const size = String(value ?? '').normalize('NFKC').toUpperCase()
    .replace(/^(?:SIZE|GRÖSSE|GROESSE)\s*:?\s*/, '').trim();
  let match = size.match(/^W?\s*(\d+)\s*W?\s*[/×X]\s*L?\s*(\d+)\s*L?$/);
  if (!match) match = size.match(/^W\s*(\d+)\s*L\s*(\d+)$/);
  if (match) return `W${Number(match[1])}L${Number(match[2])}`;
  const compact = size.replace(/[\s-]/g, '');
  const aliases = { XSMALL: 'XS', SMALL: 'S', MEDIUM: 'M', LARGE: 'L', XLARGE: 'XL', XXLARGE: 'XXL' };
  if (aliases[compact]) return aliases[compact];
  match = compact.match(/^([2-6])XL$/);
  return match ? `${'X'.repeat(Number(match[1]))}L` : compact;
}

export function parseChart(tableElement) {
  if (!tableElement?.querySelectorAll) return null;
  const headings = Array.from(tableElement.querySelectorAll('thead th'));
  const labels = headings.map((cell) => plain(cell.textContent));
  const sizeIndex = labels.findIndex((label) => ['grosse', 'groesse', 'size'].includes(label));
  if (sizeIndex < 0) return null;
  const indices = {
    chest: labels.findIndex((label) => /^(?:brust(?:umfang|weite)?|chest(?: circumference)?)$/.test(label)),
    waist: labels.findIndex((label) => /^(?:bund(?:umfang|weite)?|taille(?:numfang)?|waist(?: circumference)?)$/.test(label)),
    hip: labels.findIndex((label) => /^(?:hufte|huftumfang|hip|hips|hip circumference)$/.test(label)),
    inseam: labels.findIndex((label) => /^(?:innenbein(?:lange)?|inseam|inside leg)$/.test(label)),
    length: labels.findIndex((label) => /^(?:lange|length|lange vorn|lange vorne|front length)$/.test(label)),
  };
  const frontIndex = labels.findIndex((label) => /^(?:lange vorn|lange vorne|front length)$/.test(label));
  if (frontIndex >= 0) indices.length = frontIndex;
  const kind = indices.waist >= 0 && indices.hip >= 0 && indices.inseam >= 0 ? 'bottoms' : 'tops';
  const fields = FIELD_ORDER[kind];
  if (fields.some((field) => indices[field] < 0)) return null;
  const rows = [];
  for (const tr of tableElement.querySelectorAll('tbody tr')) {
    const cells = Array.from(tr.querySelectorAll('td'));
    const size = cells[sizeIndex]?.textContent.trim();
    if (!size || !/^(?:[2-6]?X{0,5}[SML]|XXS|\d+(?:\s*W?\s*[/×X]\s*L?\s*\d+\s*L?)?|W\d+\s*L\d+)$/i.test(size.replace(/\s+/g, ''))) continue;
    const measurements = {};
    for (const field of fields) {
      measurements[field] = cmFromValue(cells[indices[field]]?.getAttribute('data-sg-cm'));
    }
    if (fields.some((field) => measurements[field] === null)) continue;
    rows.push({ size, measurements, kind, fields: [...fields] });
  }
  if (!rows.length || new Set(rows.map((row) => normalizeSize(row.size))).size !== rows.length) return null;
  return { rows, fields: [...fields], kind, lengthLabel: headings[indices.length]?.textContent.trim() || '' };
}

function medianStep(rows, field) {
  const values = [...new Set(rows.map((row) => row.measurements[field]))].sort((a, b) => a - b);
  const steps = values.slice(1).map((value, index) => value - values[index]).sort((a, b) => a - b);
  if (!steps.length) return WIDTH_FIELDS.has(field) ? 4 : 2;
  const midpoint = Math.floor(steps.length / 2);
  return steps.length % 2 ? steps[midpoint] : (steps[midpoint - 1] + steps[midpoint]) / 2;
}

export function matchSize(rows, measurements, preference = 'same') {
  if (!Array.isArray(rows) || !rows.length || !['same', 'closer', 'looser'].includes(preference)) return null;
  const kind = rows[0].kind || (Number.isFinite(rows[0].measurements?.waist) ? 'bottoms' : 'tops');
  const fields = FIELD_ORDER[kind];
  if (!fields || fields.some((field) => !Number.isFinite(measurements?.[field]) || measurements[field] <= 0)) return null;
  const validRows = rows.filter((row) => fields.every((field) => Number.isFinite(row.measurements?.[field]) && row.measurements[field] > 0));
  if (!validRows.length) return null;
  const scales = Object.fromEntries(fields.map((field) => [field, medianStep(validRows, field)]));
  const tolerances = Object.fromEntries(fields.map((field) => [field, Math.min(WIDTH_FIELDS.has(field) ? 6 : 4.5, scales[field] / 2 + 2)]));
  for (const field of fields) {
    const values = validRows.map((row) => row.measurements[field]);
    if (measurements[field] < Math.min(...values) - 2 || measurements[field] > Math.max(...values) + 2) return null;
  }
  const score = (row) => fields.reduce((sum, field) => sum + ((row.measurements[field] - measurements[field]) / scales[field]) ** 2, 0);
  const widthField = kind === 'bottoms' ? 'waist' : 'chest';
  const rank = (a, b) => score(a) - score(b)
    || Math.abs(a.measurements[kind === 'bottoms' ? 'inseam' : 'length'] - measurements[kind === 'bottoms' ? 'inseam' : 'length'])
      - Math.abs(b.measurements[kind === 'bottoms' ? 'inseam' : 'length'] - measurements[kind === 'bottoms' ? 'inseam' : 'length'])
    || b.measurements[widthField] - a.measurements[widthField];
  const candidates = validRows.filter((row) => fields.every((field) => Math.abs(row.measurements[field] - measurements[field]) <= tolerances[field] + 1e-8));
  if (!candidates.length) return null;
  const base = [...candidates].sort(rank)[0];
  let row = base;
  if (preference !== 'same') {
    const widthFields = kind === 'bottoms' ? ['waist', 'hip'] : ['chest'];
    const key = (item) => widthFields.map((field) => item.measurements[field]).join(':');
    const groups = [...new Map(validRows.map((item) => [key(item), item])).values()]
      .sort((a, b) => a.measurements[widthField] - b.measurements[widthField]);
    const direction = preference === 'looser' ? 1 : -1;
    const adjacent = groups[groups.findIndex((item) => key(item) === key(base)) + direction];
    if (!adjacent || widthFields.some((field) => {
      const change = (adjacent.measurements[field] - base.measurements[field]) * direction;
      return change <= 0 || change > Math.min(14, scales[field] * 1.5 + 2);
    })) return null;
    const lengthField = kind === 'bottoms' ? 'inseam' : 'length';
    const adjacentRows = validRows.filter((item) => key(item) === key(adjacent)
      && Math.abs(item.measurements[lengthField] - measurements[lengthField]) <= tolerances[lengthField] + 1e-8
      && (kind !== 'bottoms' || Math.abs(item.measurements.inseam - base.measurements.inseam) <= 2));
    row = [...adjacentRows].sort(rank)[0];
    if (!row) return null;
  }
  return {
    row,
    size: row.size,
    baseSize: base.size,
    preference,
    differences: Object.fromEntries(fields.map((field) => [field, row.measurements[field] - measurements[field]])),
  };
}

export function isSizeInputAvailable(input) {
  if (!input || input.disabled || input.getAttribute('aria-disabled') === 'true' || input.dataset.optionAvailable === 'false') return false;
  const picker = input.closest('variant-picker');
  const sizeGroup = input.closest('[data-size-option]');
  const groups = Array.from(picker?.querySelectorAll?.('fieldset, .variant-option--dropdowns') || []);
  const sizeIndex = groups.indexOf(sizeGroup);
  if (sizeIndex >= 0 && groups.slice(sizeIndex + 1).some((group) => group.querySelector('input, select'))) return false;
  const connected = input.dataset.connectedProductUrl;
  const product = picker?.dataset.productUrl;
  if (connected) {
    try {
      if (!product || new URL(connected, document.baseURI).pathname !== new URL(product, document.baseURI).pathname) return false;
    } catch { return false; }
  }
  return true;
}

export function resolveSizeVariant(product, sizeIndex, size, selectedOptions) {
  const unsupported = () => ({ status: 'unsupported' });
  const hasId = (id) => typeof id === 'string' ? id.trim().length > 0
    : typeof id === 'number' && Number.isSafeInteger(id) && id > 0;
  if (!product || !hasId(product.id) || !Array.isArray(product.options) || !product.options.length
    || product.options.some((name) => typeof name !== 'string' || !name.trim())
    || new Set(product.options).size !== product.options.length
    || !Number.isInteger(sizeIndex) || sizeIndex < 0 || sizeIndex >= product.options.length
    || !['string', 'number'].includes(typeof size) || !normalizeSize(size)
    || (typeof size === 'number' && (!Number.isFinite(size) || size <= 0))
    || !Array.isArray(selectedOptions) || selectedOptions.length !== product.options.length
    || !Array.isArray(product.variants) || !product.variants.length) return unsupported();
  const count = product.options.length;
  if (product.variants.some((variant) => !variant || !hasId(variant.id)
    || typeof variant.available !== 'boolean' || !Array.isArray(variant.options) || variant.options.length !== count
    || variant.options.some((value) => typeof value !== 'string' || !value.trim()))
    || new Set(product.variants.map((variant) => String(variant.id))).size !== product.variants.length) return unsupported();

  const expected = Array(count).fill(null);
  for (let index = 0; index < count; index += 1) {
    if (index === sizeIndex) continue;
    const selected = selectedOptions[index];
    if (selected === null || selected === undefined || selected === '') {
      const values = [...new Set(product.variants.map((variant) => variant.options[index]))];
      if (values.length !== 1) return { status: 'choose_options' };
      expected[index] = values[0];
    } else {
      if (typeof selected !== 'string' || !selected.trim()) return unsupported();
      expected[index] = selected;
    }
  }
  const normalized = normalizeSize(size);
  const candidates = product.variants.filter((variant) => normalizeSize(variant.options[sizeIndex]) === normalized
    && expected.every((value, index) => index === sizeIndex || variant.options[index] === value));
  if (!candidates.length) return { status: 'missing_variant' };
  if (candidates.length !== 1) return unsupported();
  const variant = candidates[0];
  return { status: variant.available ? 'available' : 'unavailable', variant, options: [...variant.options] };
}

const BaseElement = typeof HTMLElement === 'undefined' ? class {} : HTMLElement;
const PROFILE_KEY = 'zehn.fitProfile.v2';
const DEFAULT_PROFILE = { height: 180, weight: 80, profile: 'male', chest: 0, stomach: 0, hips: 0, fit: 'regular' };

export class ZehnSizeFinder extends BaseElement {
  async connectedCallback() {
    if (this.events) return;
    this.dialog = this.querySelector('dialog');
    const template = this.querySelector('[data-zf-chart]');
    if (!this.dialog || !template || typeof this.dialog.showModal !== 'function') return;
    try { this.i18n = JSON.parse(this.querySelector('[data-zf-i18n]')?.textContent || '{}'); } catch { return; }
    const tables = Array.from(template.content.querySelectorAll('.sg-table')).filter((element) => parseChart(element));
    const table = tables.length === 1 ? tables[0] : null;
    this.chart = table ? parseChart(table) : null;
    if (this.chart) this.chart.heading = table.closest('.sg-section')?.querySelector('.sg-section__title')?.textContent || '';
    try { this.productData = JSON.parse(this.querySelector('[data-zf-product]')?.textContent || 'null'); } catch { this.productData = null; }
    try { this.colorData = JSON.parse(this.querySelector('[data-zf-color-data]')?.textContent || 'null'); } catch { this.colorData = null; }
    this.initialProductId = this.dataset.productId;
    this.operation = 0;
    this.events = new AbortController();
    const controller = this.events;
    try { this.engine = await import(this.dataset.engineUrl); } catch { this.engine = null; }
    if (controller.signal.aborted || !this.isConnected || this.events !== controller) return;
    this.state = { ...DEFAULT_PROFILE };
    this.heightUnit = 'cm';
    this.weightUnit = 'kg';
    this.remember = false;
    this.restoreProfile();
    this.step = 1;
    this.mode = 'finder';
    this.estimate = null;
    this.selectedSize = null;
    const guide = this.querySelector('[data-zf-guide]') || template;
    this.querySelector('[data-zf-chart-tables]')?.replaceChildren(guide.content.cloneNode(true));
    this.setChartUnit(this.dataset.chartDefaultUnit);
    const options = { signal: this.events.signal };
    this.addEventListener('click', (event) => this.onClick(event), options);
    this.addEventListener('input', (event) => this.onInput(event), options);
    this.addEventListener('change', (event) => this.onInput(event), options);
    this.addEventListener('keydown', (event) => this.onTabKeydown(event), options);
    document.addEventListener('click', (event) => this.onExternalClick(event), { ...options, capture: true });
    document.addEventListener('change', (event) => this.onVariantChange(event), options);
    this.dialog.addEventListener('close', () => this.afterClose(), options);
    this.dialog.addEventListener('click', (event) => { if (event.target === this.dialog) this.close(); }, options);
    this.syncControls();
    this.setStep(1, false);
    this.updateEstimate();
    this.syncColors();
    this.revealOpeners(false);
    this.openerObserver = new MutationObserver((records) => this.onMutations(records));
    this.openerObserver.observe(this.closest('.shopify-section') || document.body, {
      childList: true, subtree: true, attributes: true,
      attributeFilter: ['hidden', 'disabled', 'aria-disabled', 'data-option-available', 'data-current-checked'],
    });
  }

  disconnectedCallback() {
    if (!this.events) return;
    this.close();
    this.afterClose();
    this.openerObserver?.disconnect();
    this.events.abort();
    this.events = null;
    this.revealOpeners(true);
  }

  text(key, replacements = {}) {
    let text = this.i18n?.[key] || key;
    for (const [name, value] of Object.entries(replacements)) text = text.replaceAll(`{{${name}}}`, String(value)).replaceAll(`{${name}}`, String(value));
    return text;
  }

  restoreProfile() {
    try {
      const saved = JSON.parse(localStorage.getItem(PROFILE_KEY));
      if (!saved || saved.version !== 2 || !this.validState(saved.values)) return;
      this.state = { ...DEFAULT_PROFILE, ...saved.values };
      this.heightUnit = saved.heightUnit === 'imperial' ? 'imperial' : 'cm';
      this.weightUnit = saved.weightUnit === 'lb' ? 'lb' : 'kg';
      this.remember = true;
    } catch {}
  }

  validState(state = this.state) {
    return state && Number.isFinite(state.height) && state.height >= 140 && state.height <= 210
      && Number.isFinite(state.weight) && state.weight >= 40 && state.weight <= 160
      && ['male', 'female'].includes(state.profile) && ['slim', 'regular', 'relaxed'].includes(state.fit)
      && ['chest', 'stomach', 'hips'].every((key) => [-1, 0, 1].includes(state[key]));
  }

  saveProfile() {
    try {
      if (this.remember && this.validState()) localStorage.setItem(PROFILE_KEY, JSON.stringify({ version: 2, values: this.state, heightUnit: this.heightUnit, weightUnit: this.weightUnit }));
      else if (!this.remember) localStorage.removeItem(PROFILE_KEY);
    } catch {}
  }

  resetProfile() {
    this.remember = false;
    this.state = { ...DEFAULT_PROFILE };
    this.selectedSize = null;
    this.cartError = false;
    this.imperialDraft = null;
    this.heightUnit = 'cm';
    this.weightUnit = 'kg';
    this.saveProfile();
    this.syncControls();
    this.updateEstimate();
    const error = this.querySelector('[data-zf-error]');
    if (error) error.hidden = true;
    this.setStep(1);
  }

  ownsTrigger(trigger) {
    const source = trigger.closest('size-guide-badges');
    if (source && !source.contains(this)) return false;
    const productId = trigger.dataset.productId || source?.dataset.productId;
    if (productId !== this.dataset.productId && !(source && source.contains(this))) return false;
    const scope = trigger.closest('.shopify-section');
    const ownScope = this.closest('.shopify-section');
    return !scope || !ownScope || scope === ownScope;
  }

  revealOpeners(hidden) {
    document.querySelectorAll('[data-zf-open]').forEach((trigger) => {
      if (this.ownsTrigger(trigger) && trigger.hidden !== hidden) trigger.hidden = hidden;
    });
  }

  onMutations(records) {
    const added = records.flatMap((record) => Array.from(record.addedNodes || [])).filter((node) => node.nodeType === 1);
    const changedOpener = records.some((record) => record.type === 'attributes' && record.target.matches('[data-zf-open]') && record.target.hidden && this.ownsTrigger(record.target));
    if (changedOpener || added.some((node) => node.matches('[data-zf-open]') || node.querySelector('[data-zf-open]'))) this.revealOpeners(false);
    const changedPicker = records.some((record) => record.target.closest?.('variant-picker')?.dataset.productId === this.dataset.productId)
      || added.some((node) => node.matches('variant-picker') && node.dataset.productId === this.dataset.productId
        || Array.from(node.querySelectorAll?.('variant-picker') || []).some((picker) => picker.dataset.productId === this.dataset.productId));
    if (changedPicker) { this.syncColors(); if (this.estimate || this.result) this.updateAvailability(); }
  }

  findVariantPicker() {
    const ownSection = this.closest('.shopify-section');
    const pickers = Array.from(document.querySelectorAll('variant-picker')).filter((picker) => picker.dataset.productId === this.dataset.productId);
    const local = pickers.filter((picker) => ownSection && picker.closest('.shopify-section') === ownSection);
    const scope = local.length ? local : pickers;
    return scope.length === 1 ? scope[0] : null;
  }

  colorInputs(picker = this.findVariantPicker()) {
    const index = this.colorData?.index;
    const name = this.productData?.options?.[index];
    if (!picker || !Number.isInteger(index) || index < 0 || !name) return [];
    return Array.from(picker.querySelectorAll('input[type="radio"], select option')).filter((input) =>
      input.dataset.optionName === name || Number(input.dataset.inputId?.split('-')[0]) === index + 1);
  }

  sameColorProduct(color, input) {
    if (!this.productData?.url || String(this.productData.id) !== this.dataset.productId) return false;
    return [color?.productUrl, input?.dataset.connectedProductUrl].every((url) => {
      if (!url) return true;
      try { return new URL(url, document.baseURI).pathname === new URL(this.productData.url, document.baseURI).pathname; }
      catch { return false; }
    });
  }

  syncColors() {
    const toggle = this.querySelector('[data-zf-color-toggle]');
    if (!toggle || !Array.isArray(this.colorData?.values)) return;
    const inputs = this.colorInputs();
    const selected = inputs.filter((input) => input.tagName === 'OPTION' ? input.selected : input.checked);
    const value = selected.length === 1 ? selected[0].value : this.colorData.values.length === 1 ? this.colorData.values[0].value : null;
    const color = this.colorData.values.find((item) => item.value === value);
    const label = this.querySelector('[data-zf-selected-color]');
    if (label) label.textContent = color ? ` (${color.label || color.value})` : '';
    this.colorValue = color?.value || null;
    if (color) {
      toggle.setAttribute('aria-label', `${this.text('change_color')}: ${color.label || color.value}`);
      const swatch = this.querySelector('[data-zf-current-swatch]');
      if (swatch) swatch.style.setProperty('--zf-swatch', color.swatch || '');
      const image = this.querySelector('.zf-product__image');
      if (image && color.image?.src && image.getAttribute('src') !== color.image.src) {
        image.removeAttribute('srcset');
        image.setAttribute('src', color.image.src);
        image.setAttribute('alt', color.image.alt || color.label || color.value);
      }
    }
    const options = this.colorData.values.filter((item) => inputs.some((input) => input.value === item.value && this.sameColorProduct(item, input)));
    toggle.hidden = !color || options.length < 2;
    toggle.disabled = !!this.adding;
    const choices = this.querySelector('[data-zf-color-options]');
    if (choices) {
      const existing = Array.from(choices.querySelectorAll('[data-zf-color]'));
      if (JSON.stringify(existing.map((button) => button.dataset.zfColor)) !== JSON.stringify(options.map((item) => item.value))) {
        choices.replaceChildren();
        for (const item of options) {
          const button = document.createElement('button');
          button.type = 'button'; button.className = 'zf-color-choice'; button.dataset.zfColor = item.value;
          button.setAttribute('aria-label', item.label || item.value);
          const dot = document.createElement('span'); dot.className = 'zf-color-dot'; dot.setAttribute('aria-hidden', 'true'); dot.style.setProperty('--zf-swatch', item.swatch || '');
          const name = document.createElement('span'); name.textContent = item.label || item.value;
          button.append(dot, name); choices.append(button);
        }
      }
      for (const button of choices.querySelectorAll('[data-zf-color]')) {
        button.setAttribute('aria-pressed', String(button.dataset.zfColor === this.colorValue));
        button.disabled = !!this.adding;
      }
    }
    if (toggle.hidden) this.setColorPanel(false);
  }

  setColorPanel(open) {
    const panel = this.querySelector('[data-zf-color-panel]');
    const toggle = this.querySelector('[data-zf-color-toggle]');
    if (panel) panel.hidden = !open;
    if (toggle) toggle.setAttribute('aria-expanded', String(!!open));
  }

  selectColor(value) {
    if (this.adding) return false;
    const color = this.colorData?.values?.find((item) => item.value === value);
    const inputs = this.colorInputs().filter((input) => input.value === value);
    if (!color || inputs.length !== 1 || !this.sameColorProduct(color, inputs[0]) || inputs[0].disabled) return false;
    this.operation = (this.operation || 0) + 1;
    this.cartError = false;
    const input = inputs[0];
    if (input.tagName === 'OPTION') {
      const select = input.closest('select');
      if (!select) return false;
      if (!input.selected) { select.value = input.value; select.dispatchEvent(new Event('change', { bubbles: true })); }
    } else if (!input.checked) input.click();
    this.syncColors();
    this.updateAvailability();
    this.setColorPanel(false);
    this.querySelector('[data-zf-color-toggle]')?.focus({ preventScroll: true });
    return true;
  }

  onVariantChange(event) {
    const picker = event.target.closest?.('variant-picker');
    if (!picker || picker !== this.findVariantPicker()) return;
    const previous = this.colorValue;
    this.syncColors();
    if (previous !== this.colorValue) { this.operation = (this.operation || 0) + 1; this.cartError = false; }
    this.updateAvailability();
  }

  onExternalClick(event) {
    const trigger = event.target.closest?.('[data-zf-open], [data-sg-open="size-guide"]');
    if (!trigger || !this.ownsTrigger(trigger)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.open(trigger);
  }

  open(trigger) {
    if (this.dialog.open) return;
    this.setMode('finder');
    this.lastFocused = trigger || document.activeElement;
    this.dialog.showModal();
    if (!scrollLocks.size) previousOverflow = document.body.style.overflow;
    scrollLocks.add(this);
    document.body.style.overflow = 'hidden';
    this.syncColors();
    this.updateAvailability();
    this.querySelector('[data-zf-close]')?.focus({ preventScroll: true });
  }

  close() { this.operation = (this.operation || 0) + 1; if (this.dialog?.open) this.dialog.close(); }

  afterClose() {
    this.operation = (this.operation || 0) + 1;
    this.setColorPanel(false);
    if (scrollLocks.delete(this) && !scrollLocks.size) document.body.style.overflow = previousOverflow;
    const trigger = this.lastFocused;
    this.lastFocused = null;
    if (trigger?.isConnected) trigger.focus({ preventScroll: true });
  }

  onClick(event) {
    const button = event.target.closest('button');
    if (!button || !this.contains(button)) return;
    if (this.adding && !button.hasAttribute('data-zf-close')) return;
    if (button.hasAttribute('data-zf-close')) this.close();
    else if (button.hasAttribute('data-zf-next')) this.advance();
    else if (button.hasAttribute('data-zf-back')) this.setStep(Math.max(1, this.step - 1));
    else if (button.hasAttribute('data-zf-change')) this.setStep(1);
    else if (button.hasAttribute('data-zf-reset') || button.hasAttribute('data-zf-restart')) this.resetProfile();
    else if (button.hasAttribute('data-zf-color-toggle')) this.setColorPanel(button.getAttribute('aria-expanded') !== 'true');
    else if (button.hasAttribute('data-zf-color')) this.selectColor(button.dataset.zfColor);
    else if (button.hasAttribute('data-zf-select')) this.selectSize();
    else if (button.hasAttribute('data-zf-add')) this.addToBag();
    else if (button.hasAttribute('data-zf-mode')) this.setMode(button.dataset.zfMode);
    else if (button.hasAttribute('data-zf-chart-unit')) this.setChartUnit(button.dataset.zfChartUnit);
    else if (button.hasAttribute('data-zf-show-chart') || button.hasAttribute('data-zf-result-chart')) this.setMode('chart');
    else if (button.hasAttribute('data-zf-show-finder')) this.setMode('finder');
    else if (button.hasAttribute('data-zf-height-unit')) { this.heightUnit = button.dataset.zfHeightUnit; this.syncControls(); this.saveProfile(); }
    else if (button.hasAttribute('data-zf-weight-unit')) { this.weightUnit = button.dataset.zfWeightUnit; this.syncControls(); this.saveProfile(); }
    else if (button.hasAttribute('data-zf-adjust')) {
      const field = button.dataset.zfAdjust;
      const delta = Number(button.dataset.direction);
      const factor = field === 'height' && this.heightUnit === 'imperial' ? 2.54 : field === 'weight' && this.weightUnit === 'lb' ? 0.45359237 : 1;
      const current = Number.isFinite(this.state[field]) ? this.state[field] : DEFAULT_PROFILE[field];
      if (field === 'height') this.imperialDraft = null;
      this.state[field] = Math.max(field === 'height' ? 140 : 40, Math.min(field === 'height' ? 210 : 160, current + delta * factor));
      this.syncControls(); this.updateEstimate(); this.saveProfile();
    } else if (button.hasAttribute('data-zf-size')) {
      this.cartError = false;
      this.selectedSize = button.dataset.zfSize;
      this.renderResult();
    }
  }

  onInput(event) {
    const input = event.target;
    this.operation = (this.operation || 0) + 1;
    const numeric = (field) => field?.value?.trim() === '' ? null : Number.isFinite(Number(field?.value)) ? Number(field.value) : NaN;
    if (input.matches('[data-zf-height-range]')) { this.state.height = Number(input.value); this.syncControls(); }
    else if (input.matches('[data-zf-weight-range]')) { this.state.weight = Number(input.value); this.syncControls(); }
    else if (input.matches('[data-zf-height]')) { this.imperialDraft = null; this.state.height = numeric(input); }
    else if (input.matches('[data-zf-feet], [data-zf-inches]')) {
      const feetInput = this.querySelector('[data-zf-feet]');
      const inchInput = this.querySelector('[data-zf-inches]');
      const feet = numeric(feetInput);
      const inches = numeric(inchInput);
      this.imperialDraft = { feet: feetInput.value, inches: inchInput.value };
      this.state.height = feet === null || inches === null ? null : Number.isInteger(feet) && Number.isInteger(inches) && inches >= 0 && inches < 12 ? (feet * 12 + inches) * 2.54 : NaN;
    } else if (input.matches('[data-zf-weight]')) { const weight = numeric(input); this.state.weight = weight === null ? null : weight * (this.weightUnit === 'lb' ? 0.45359237 : 1); }
    else if (input.matches('[data-zf-profile]')) { this.state.profile = input.value; this.syncControls(); }
    else if (input.matches('[data-zf-shape]')) this.state[input.dataset.zfShape] = Number(input.value);
    else if (input.matches('[data-zf-fit]')) this.state.fit = input.value;
    else if (input.matches('[data-zf-remember]')) { this.remember = input.checked; this.syncControls(); }
    else return;
    const heightRange = this.querySelector('[data-zf-height-range]');
    const weightRange = this.querySelector('[data-zf-weight-range]');
    if (heightRange && Number.isFinite(this.state.height) && this.state.height >= 140 && this.state.height <= 210) { heightRange.value = this.state.height; heightRange.setAttribute('aria-valuetext', `${Math.round(this.state.height)} cm`); }
    if (weightRange && Number.isFinite(this.state.weight) && this.state.weight >= 40 && this.state.weight <= 160) { weightRange.value = this.state.weight; weightRange.setAttribute('aria-valuetext', `${Math.round(this.state.weight * (this.weightUnit === 'lb' ? 2.20462262 : 1))} ${this.weightUnit}`); }
    this.cartError = false;
    this.updateEstimate();
    this.saveProfile();
    const error = this.querySelector('[data-zf-error]');
    if (error && this.validState()) error.hidden = true;
  }

  syncControls() {
    const height = this.querySelector('[data-zf-height]');
    const feet = this.querySelector('[data-zf-feet]');
    const inches = this.querySelector('[data-zf-inches]');
    const weight = this.querySelector('[data-zf-weight]');
    const validHeight = Number.isFinite(this.state.height);
    const validWeight = Number.isFinite(this.state.weight);
    const totalInches = validHeight ? Math.round(this.state.height / 2.54) : null;
    if (height) height.value = validHeight ? String(Math.round(this.state.height * 10) / 10) : '';
    if (feet) feet.value = validHeight ? String(Math.floor(totalInches / 12)) : this.imperialDraft?.feet || '';
    if (inches) inches.value = validHeight ? String(totalInches % 12) : this.imperialDraft?.inches || '';
    if (weight) weight.value = validWeight ? String(Math.round(this.state.weight * (this.weightUnit === 'lb' ? 2.20462262 : 1) * 10) / 10) : '';
    const cm = this.querySelector('[data-zf-cm-fields]');
    const imperial = this.querySelector('[data-zf-imperial-fields]');
    if (cm) cm.hidden = this.heightUnit !== 'cm';
    if (imperial) imperial.hidden = this.heightUnit !== 'imperial';
    if (weight) { weight.min = this.weightUnit === 'lb' ? '88.2' : '40'; weight.max = this.weightUnit === 'lb' ? '352.7' : '160'; weight.step = '0.1'; }
    const suffix = this.querySelector('[data-zf-weight-suffix]');
    if (suffix) suffix.textContent = this.weightUnit;
    this.querySelectorAll('[data-zf-height-unit]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.zfHeightUnit === this.heightUnit)));
    this.querySelectorAll('[data-zf-weight-unit]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.zfWeightUnit === this.weightUnit)));
    this.querySelectorAll('[data-zf-profile]').forEach((input) => { input.checked = input.value === this.state.profile; });
    this.querySelectorAll('[data-zf-shape]').forEach((input) => { input.checked = Number(input.value) === this.state[input.dataset.zfShape]; });
    this.querySelectorAll('[data-zf-fit]').forEach((input) => { input.checked = input.value === this.state.fit; });
    const remember = this.querySelector('[data-zf-remember]');
    if (remember) remember.checked = this.remember;
    const reset = this.querySelector('[data-zf-reset]');
    if (reset) reset.hidden = !this.remember;
    const heightRange = this.querySelector('[data-zf-height-range]');
    const weightRange = this.querySelector('[data-zf-weight-range]');
    if (heightRange && validHeight && this.state.height >= 140 && this.state.height <= 210) { heightRange.value = this.state.height; heightRange.setAttribute('aria-valuetext', this.heightUnit === 'cm' ? `${Math.round(this.state.height)} cm` : `${Math.floor(totalInches / 12)} ft ${totalInches % 12} in`); }
    if (weightRange && validWeight && this.state.weight >= 40 && this.state.weight <= 160) { weightRange.value = this.state.weight; weightRange.setAttribute('aria-valuetext', `${Math.round(this.state.weight * (this.weightUnit === 'lb' ? 2.20462262 : 1))} ${this.weightUnit}`); }
  }

  assessmentText() {
    const assessment = this.assessment || {};
    const key = assessment.reason === 'invalid_input' ? assessment.inputIssue === 'incomplete' ? 'reason_incomplete' : assessment.inputIssue === 'unsupported_proportions' ? 'reason_unreliable_input' : 'reason_invalid'
      : ({ below_range: 'reason_below_chart', above_range: 'reason_above_chart', proportion_mismatch: 'reason_proportions', missing_length: 'reason_missing_length', unsupported_chart: 'reason_unsupported', engine_unavailable: 'reason_engine_unavailable' })[assessment.reason] || 'no_match_note';
    return this.text(key, { length: assessment.lengthSize || '' });
  }

  updateEstimate() {
    this.result = null;
    if (!this.validState()) this.assessment = { estimate: null, reason: 'invalid_input', inputIssue: this.state.height === null || this.state.weight === null ? 'incomplete' : 'bounds' };
    else if (!this.engine) this.assessment = { estimate: null, reason: 'engine_unavailable' };
    else if (this.engine.assessFit) this.assessment = this.engine.assessFit(this.state, this.chart);
    else { const estimate = this.engine.estimateFit(this.state, this.chart); this.assessment = { estimate, reason: estimate ? 'matched' : 'unsupported_chart' }; }
    this.estimate = this.assessment.estimate;
    const live = this.querySelector('[data-zf-live-size]');
    if (live) { live.textContent = this.estimate?.size || this.text(this.assessment.inputIssue === 'incomplete' ? 'live_incomplete' : ['engine_unavailable', 'unsupported_chart'].includes(this.assessment.reason) ? 'live_chart_only' : 'live_no_match'); live.toggleAttribute('data-empty', !this.estimate); }
    const status = this.querySelector('[data-zf-live-status]');
    if (status) { status.hidden = !!this.estimate; status.textContent = this.estimate ? '' : this.assessmentText(); }
    const figure = this.querySelector('[data-zf-figure]');
    if (figure) {
      const height = Number.isFinite(this.state.height) ? this.state.height : 180;
      const weight = Number.isFinite(this.state.weight) ? this.state.weight : 80;
      figure.style.setProperty('--zf-body-height', String(Math.max(.82, Math.min(1.12, height / 180))));
      figure.style.setProperty('--zf-body-width', String(Math.max(.78, Math.min(1.28, Math.sqrt(weight / 80)))));
      figure.dataset.profile = this.state.profile;
    }
  }

  advance() {
    if (!this.validState()) {
      const error = this.querySelector('[data-zf-error]');
      this.updateEstimate();
      if (error) { error.hidden = false; error.textContent = this.assessment.inputIssue === 'incomplete' ? this.text('reason_incomplete') : this.text('bounds_error'); }
      const feet = this.querySelector('[data-zf-feet]');
      const invalidFeet = feet?.value === '' || !Number.isInteger(Number(feet?.value)) || Number(feet?.value) < 4 || Number(feet?.value) > 6;
      const field = !Number.isFinite(this.state.height) || this.state.height < 140 || this.state.height > 210 ? this.heightUnit === 'imperial' ? this.querySelector(invalidFeet ? '[data-zf-feet]' : '[data-zf-inches]') : this.querySelector('[data-zf-height]') : this.querySelector('[data-zf-weight]');
      field?.focus();
      return;
    }
    this.saveProfile();
    this.updateEstimate();
    if (this.step === 3) {
      this.selectedSize = this.estimate?.size || null;
      this.renderResult();
    }
    this.setStep(Math.min(4, this.step + 1));
  }

  setMode(mode) {
    this.mode = mode === 'chart' ? 'chart' : 'finder';
    if (this.dialog?.dataset) this.dialog.dataset.mode = this.mode;
    const finder = this.querySelector('[data-zf-finder]');
    const chart = this.querySelector('[data-zf-chart-view]');
    if (finder) finder.hidden = this.mode !== 'finder';
    if (chart) chart.hidden = this.mode !== 'chart';
    this.querySelectorAll('[data-zf-mode]').forEach((tab) => {
      const active = tab.dataset.zfMode === this.mode;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
    });
    if (this.mode === 'chart') this.setColorPanel(false);
    const chartButton = this.querySelector('[data-zf-show-chart]');
    if (chartButton) chartButton.hidden = this.mode === 'chart';
    this.syncNavigation();
    this.scrollTop();
  }

  onTabKeydown(event) {
    const tab = event.target.closest?.('[data-zf-mode]');
    if (!tab || !this.contains(tab) || this.adding) return;
    const tabs = Array.from(this.querySelectorAll('[data-zf-mode]'));
    const index = tabs.indexOf(tab);
    let next;
    if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
    else if (event.key === 'ArrowLeft') next = (index + tabs.length - 1) % tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;
    else return;
    event.preventDefault();
    this.setMode(tabs[next].dataset.zfMode);
    tabs[next].focus({ preventScroll: true });
  }

  setChartUnit(unit) {
    this.chartUnit = unit === 'inch' ? 'inch' : 'cm';
    const panel = this.querySelector('[data-zf-chart-view]');
    if (!panel) return;
    panel.querySelectorAll('[data-zf-chart-unit]').forEach((button) => {
      const active = button.dataset.zfChartUnit === this.chartUnit;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    });
    const suffix = this.chartUnit === 'inch' ? this.dataset.inchLabel || 'inch' : this.dataset.cmLabel || 'cm';
    panel.querySelectorAll('[data-sg-unit-label]').forEach((label) => { label.textContent = `(${suffix})`; });
    panel.querySelectorAll('[data-sg-cm]').forEach((cell) => {
      const raw = cell.getAttribute('data-sg-cm').trim();
      const cm = cmFromValue(raw);
      cell.textContent = this.chartUnit === 'inch' && cm !== null
        ? (cm / 2.54).toFixed(2).replace(/\.?0+$/, '').replace('.', this.dataset.decimalSeparator || ',')
        : raw;
    });
  }

  setStep(step, focus = true) {
    this.step = step;
    this.setMode('finder');
    this.querySelectorAll('[data-zf-step]').forEach((section) => { section.hidden = Number(section.dataset.zfStep) !== step; });
    const label = this.querySelector('[data-zf-step-label]');
    if (label) { label.hidden = step === 4; label.textContent = step === 4 ? '' : this.text(`step_${step}`); }
    const progress = this.querySelector('[data-zf-progress]');
    if (progress) { progress.dataset.step = Math.min(step, 3); progress.hidden = step === 4; Array.from(progress.children).forEach((segment, index) => { segment.classList.toggle('is-complete', index < step); if (index === step - 1) segment.setAttribute('aria-current', 'step'); else segment.removeAttribute('aria-current'); }); }
    this.syncNavigation();
    if (focus && this.dialog?.open) this.querySelector(`[data-zf-step="${step}"] h2`)?.focus({ preventScroll: true });
    this.scrollTop();
  }

  scrollTop() { const scroll = this.querySelector('.zf-scroll'); if (scroll) scroll.scrollTop = 0; }

  syncNavigation() {
    const footer = this.querySelector('[data-zf-footer]');
    if (footer) footer.hidden = this.mode === 'chart';
    const back = this.querySelector('[data-zf-back]');
    const next = this.querySelector('[data-zf-next]');
    const add = this.querySelector('[data-zf-add]');
    if (back) back.hidden = this.step === 1 || this.step === 4 || this.mode === 'chart';
    if (next) { next.hidden = this.step === 4 || this.mode === 'chart'; next.textContent = `${this.text(this.step === 3 ? 'find_size' : 'next')} →`; }
    if (add) add.hidden = this.step !== 4 || this.mode === 'chart' || !this.result;
    this.updateAvailability();
  }

  renderResult() {
    const row = Array.isArray(this.chart?.rows)
      ? this.chart.rows.find((item) => normalizeSize(item.size) === normalizeSize(this.selectedSize)) : null;
    this.result = this.estimate && row ? { ...this.estimate, row, size: row.size } : null;
    const size = this.querySelector('[data-zf-result-size]');
    const label = this.querySelector('[data-zf-result-label]');
    const note = this.querySelector('[data-zf-result-note]');
    if (size) size.textContent = this.result?.size || '';
    if (size) size.toggleAttribute('data-complex', (this.result?.size?.length || 0) > 4);
    if (label) label.textContent = this.text(!this.result ? 'no_match' : this.result.size === this.estimate.size ? 'result_estimate' : 'adjusted_size');
    if (note) note.textContent = this.result ? this.text('result_note') : this.assessmentText();
    const ring = this.querySelector('[data-zf-result-ring]');
    if (ring) ring.hidden = !this.result;
    const chartAction = this.querySelector('[data-zf-result-chart]');
    if (chartAction) chartAction.hidden = !!this.result;
    const boundary = this.querySelector('[data-zf-boundary]');
    if (boundary) { const reference = !this.result && this.assessment?.boundary; boundary.hidden = !reference; boundary.textContent = reference ? this.text(reference.direction === 'below' ? 'boundary_below' : 'boundary_above', { size: reference.size }) : ''; }
    const fitNote = this.querySelector('[data-zf-fit-note]');
    if (fitNote) fitNote.textContent = this.result ? this.text(this.result.size === this.estimate.size ? this.state.stomach === 1 && this.estimate.kind === 'bottoms' ? 'fit_note_belly' : `fit_note_${this.state.fit}` : 'adjusted_note') + (this.estimate.lengthSize ? ` ${this.text('length_note', { length: this.estimate.lengthSize })}` : '') : '';
    for (const selector of ['.zf-result-estimate', '.zf-size-hint', '.zf-fit-note']) {
      const summary = this.querySelector(selector);
      if (summary) summary.hidden = !this.result;
    }
    this.renderSizes();
    this.renderScales();
    this.updateAvailability();
  }

  renderSizes() {
    const container = this.querySelector('[data-zf-sizes]');
    if (!container) return;
    container.replaceChildren();
    container.hidden = !this.result;
    if (!this.result) return;
    const length = this.estimate.lengthSize;
    const rows = length ? this.chart.rows.filter((row) => normalizeSize(row.size).endsWith(length)) : this.chart.rows;
    container.hidden = !rows.length;
    for (const row of rows) {
      const button = document.createElement('button');
      button.type = 'button'; button.className = 'zf-size-chip'; button.dataset.zfSize = row.size;
      button.setAttribute('aria-pressed', String(row.size === this.result?.size));
      const recommended = row.size === this.estimate.size;
      button.classList.toggle('is-recommended', recommended);
      button.textContent = length ? normalizeSize(row.size).split('L')[0] : row.size;
      button.setAttribute('aria-label', row.size);
      if (recommended) { const mark = document.createElement('small'); mark.textContent = this.text('you'); button.append(mark); }
      container.append(button);
    }
  }

  renderScales() {
    const container = this.querySelector('[data-zf-fit-scales]');
    if (!container) return;
    container.replaceChildren();
    const descriptions = this.result ? this.engine.describeFit(this.result.row, this.estimate, this.chart) : [];
    container.hidden = !descriptions.length;
    if (!descriptions.length) return;
    const headings = document.createElement('div');
    headings.className = 'zf-scale-labels';
    for (const key of ['snug', 'balanced', 'roomy']) { const item = document.createElement('span'); item.textContent = this.text(key); headings.append(item); }
    container.append(headings);
    for (const item of descriptions) {
      const row = document.createElement('div'); row.className = 'zf-fit-scale';
      const label = document.createElement('span'); label.className = 'zf-fit-scale__label'; label.textContent = this.text(item.key);
      const track = document.createElement('span'); track.className = 'zf-fit-scale__track'; track.setAttribute('aria-hidden', 'true');
      const dot = document.createElement('span'); dot.className = 'zf-fit-scale__dot'; dot.style.left = `${Math.max(5, Math.min(95, item.position))}%`; track.append(dot);
      const value = document.createElement('span'); value.className = 'zf-fit-scale__value'; value.textContent = this.text(item.label);
      row.append(label, track, value); container.append(row);
    }
  }

  findSizeInput() {
    if (!this.result) return null;
    const ownSection = this.closest('.shopify-section');
    const pickers = Array.from(document.querySelectorAll('variant-picker')).filter((picker) => picker.dataset.productId === this.dataset.productId);
    const local = pickers.filter((picker) => ownSection && picker.closest('.shopify-section') === ownSection);
    const scope = local.length ? local : pickers;
    if (scope.length !== 1) return null;
    const inputs = Array.from(scope[0].querySelectorAll('[data-size-option] input[type="radio"]')).filter((input) => normalizeSize(input.value) === normalizeSize(this.result.size));
    return inputs.length === 1 ? inputs[0] : null;
  }

  getVariantResolution() {
    const input = this.findSizeInput();
    if (!this.result || !input) return { status: 'unsupported' };
    const picker = input.closest('variant-picker');
    if (this.initialProductId && this.initialProductId !== this.dataset.productId) return { status: 'unsupported' };
    if (!this.productData) {
      if (isSizeInputAvailable(input) && input.dataset.variantId) return { status: 'available', variant: { id: input.dataset.variantId }, options: [] };
      return { status: input.disabled || input.getAttribute('aria-disabled') === 'true' || input.dataset.optionAvailable === 'false' ? 'unavailable' : 'choose_options' };
    }
    if (String(this.productData.id) !== this.dataset.productId || !picker || !Array.isArray(this.productData.options) || !Array.isArray(this.productData.variants)) return { status: 'unsupported' };
    const selected = Array.from(picker.querySelectorAll('input[type="radio"]:checked, select'));
    const sameProduct = (node) => {
      const connected = node.dataset.connectedProductUrl;
      if (!connected) return true;
      try { return new URL(connected, document.baseURI).pathname === new URL(this.productData.url, document.baseURI).pathname; } catch { return false; }
    };
    if (!sameProduct(input) || selected.some((node) => !sameProduct(node))) return { status: 'unsupported' };
    const position = Number(input.dataset.inputId?.split('-')[0]);
    const sizeIndex = Number.isInteger(position) && position > 0 ? position - 1 : this.productData.options.indexOf(input.dataset.optionName);
    const selections = this.productData.options.map((name, index) => {
      const matches = selected.filter((node) => node.dataset.optionName === name || Number(node.dataset.inputId?.split('-')[0]) === index + 1);
      return matches.length === 1 ? matches[0].value : null;
    });
    return resolveSizeVariant(this.productData, sizeIndex, this.result.size, selections);
  }

  updateAvailability() {
    const input = this.findSizeInput();
    const resolution = this.getVariantResolution();
    const available = resolution.status === 'available';
    const status = this.querySelector('[data-zf-availability]');
    const messages = { available: 'available', unavailable: 'unavailable', choose_options: 'status_choose_options', missing_variant: 'status_missing_variant', unsupported: 'status_choose_options' };
    if (status) status.textContent = this.cartError ? this.text('cart_not_ready') : !this.result ? '' : this.text(messages[resolution.status] || 'unsupported');
    const select = this.querySelector('[data-zf-select]');
    if (select) { select.hidden = this.step !== 4 || this.mode !== 'finder' || !this.result || !input; select.disabled = !available || !!this.adding; }
    const add = this.querySelector('[data-zf-add]');
    if (add) { add.disabled = !available || !!this.adding; add.textContent = this.text(this.adding ? 'adding' : 'add_to_bag'); }
  }

  applySize(resolution = this.getVariantResolution()) {
    const input = this.findSizeInput();
    if (!input || resolution.status !== 'available') return null;
    const fieldset = input.closest('fieldset');
    fieldset?.classList.add('is-expanded');
    fieldset?.querySelector('.variant-option__more-toggle')?.setAttribute('aria-expanded', 'true');
    const id = String(resolution.variant.id);
    input.click();
    return id;
  }

  async selectSize() {
    return this.completeSelection(false);
  }

  async addToBag() {
    return this.completeSelection(true);
  }

  async completeSelection(addToCart) {
    if (this.adding) return;
    this.cartError = false;
    const expected = this.getVariantResolution();
    if (expected.status !== 'available') { this.updateAvailability(); return; }
    this.adding = true;
    const operation = this.operation = (this.operation || 0) + 1;
    const productId = this.dataset?.productId;
    const size = this.result?.size;
    this.updateAvailability();
    const id = this.applySize(expected);
    const scope = this.closest('.shopify-section');
    try {
      if (!id || !scope) throw new Error('variant');
      const currentMatches = () => {
        const current = this.getVariantResolution();
        return this.operation === operation && this.dataset?.productId === productId && this.result?.size === size
          && current.status === 'available' && String(current.variant.id) === id
          && JSON.stringify(current.options) === JSON.stringify(expected.options);
      };
      const form = await new Promise((resolve, reject) => {
        const started = Date.now();
        const check = () => {
          if (!scope.isConnected || !this.isConnected || !currentMatches()) { reject(new Error('changed')); return; }
          const forms = Array.from(scope.querySelectorAll('form[data-bb-form]'));
          const matching = forms.filter((candidate) => candidate.querySelector('input[name="id"]')?.value === id && candidate.querySelector('[data-bb-add]:not(:disabled)'));
          if (matching.length === 1 && this.findSizeInput()?.checked) resolve(matching[0]);
          else if (Date.now() - started > 5000) reject(new Error('variant'));
          else setTimeout(check, 80);
        };
        check();
      });
      if (!currentMatches() || form.querySelector('input[name="id"]')?.value !== id) throw new Error('changed');
      this.lastFocused = null;
      this.close();
      if (addToCart) form.querySelector('[data-bb-add]').click();
    } catch {
      this.cartError = this.operation === operation;
      const status = this.querySelector('[data-zf-availability]');
      if (status) status.textContent = this.text('cart_not_ready');
    } finally { this.adding = false; this.updateAvailability(); }
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('zehn-size-finder')) customElements.define('zehn-size-finder', ZehnSizeFinder);
